import { useState, useEffect, useRef } from 'react';

/**
 * A custom React hook for implementing infinite scroll behavior using the Intersection Observer API.
 *
 * @param {boolean} hasNextPage - Indicates whether there are more items to load.
 * @param {number} delay - The delay in milliseconds before triggering the loadMore function.
 * @param {number} pageSize - The number of items to load in each request.
 * @param {number} pageNumber - The current page number.
 * @param {function} loadMore - The function to be triggered when more items need to be loaded.
 * @returns {Object} scrollContainerRef - A React ref that should be attached to the scrollable container element.
 */
const useInfiniteScroll = ({
    hasNextPage,
    delay,
    pageSize,
    pageNumber,
    loadMore,
    scrollTriggerGap = 100,
}) => {
    const [isFetching, setIsFetching] = useState(false);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        /**
         * Handles the scroll event and triggers the loadMore function when the user scrolls
         * to the bottom of the scrollable container.
         */
        const handleScroll = () => {
            if (!isFetching && hasNextPage) {
                const container = scrollContainerRef.current;
                if (container) {
                    const { scrollTop, scrollHeight, clientHeight } = container;

                    // Check if the user has scrolled to the bottom (within 100 pixels)
                    if (
                        scrollTop + clientHeight >=
                        scrollHeight - scrollTriggerGap
                    ) {
                        setIsFetching(true);

                        // Use a delay before triggering the loadMore function
                        setTimeout(() => {
                            loadMore(pageSize, pageNumber + 1);
                            setIsFetching(false);
                        }, delay);
                    }
                }
            }
        };

        // Attach the scroll event listener to the scrollable container
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
        }

        // Remove the scroll event listener when the component unmounts
        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, [hasNextPage, isFetching, delay, pageSize, pageNumber, loadMore]);

    // Return the ref that should be attached to the scrollable container element
    return scrollContainerRef;
};

export default useInfiniteScroll;
